<template>
  <BlockContainer class="p-5">
    <ValidationObserver v-slot="{ invalid, dirty }">
      <div class="row align-items-center">
        <div class="col col-lg-8">
          <h5>Leieobjektets egenskaper</h5>
        </div>

        <div class="col edit-buttons text-right">
          <div v-if="editMode">
            <CancelButton class="mr-1" @click.native="editMode = false" />
            <SaveButton
              :loading="loading"
              @click.native="patchRentalObject"
              :disabled="invalid || !dirty"
              class="ml-1"
            />
          </div>

          <EditButton v-else @click.native="setEditModeAndLocalCopy" />
        </div>
      </div>

      <div class="row mt-5">
        <div class="col">
          <strong>Boligtype</strong>
        </div>

        <div class="col">
          <div v-if="loaded">
            <ValidationProvider
              v-if="editMode"
              name="Boligtype"
              :rules="validationRules.rentalObject.rentalObject.street"
              v-slot="{ errors }"
            >
              <!-- add value -->
              <hl-Select
                size="sm"
                :hasMarginBottom="false"
                v-model="localCopy.details.type"
                placeholder="velg boligtype"
                :options="propertyTypes"
                :error="errors[0]"
              ></hl-Select>
            </ValidationProvider>
            <span v-else>{{ mapPropertyTypes(rentalObject.details.type) }}</span>
          </div>

          <hl-Mask v-else :width="180" :height="15"></hl-Mask>
        </div>
      </div>

      <div class="row mt-4">
        <div class="col">
          <strong>Størrelse / Areal (m²)</strong>
        </div>

        <div class="col">
          <div v-if="loaded">
            <ValidationProvider
              v-if="editMode"
              name="Størrelse"
              :rules="validationRules.rentalObject.rentalObject.areaSize"
              v-slot="{ errors }"
            >
              <hl-Input type="number" size="sm" v-model="localCopy.details.areaSize" :error="errors[0]" />
            </ValidationProvider>

            <span v-else>
              <span>{{ rentalObject.details.areaSize }}</span>
            </span>
          </div>

          <hl-Mask v-else :width="10" :height="15"></hl-Mask>
        </div>
      </div>

      <div class="row mt-4">
        <div class="col">
          <strong>Energikarakter</strong>
        </div>

        <div class="col">
          <div v-if="loaded">
            <ValidationProvider v-if="editMode" name="energyCharacter">
              <hl-Select
                size="sm"
                :hasMarginBottom="false"
                v-model="localCopy.details.energyScore"
                placeholder="velg energikarakter"
                :options="[
                  { name: 'A', value: 'A' },
                  { name: 'B', value: 'B' },
                  { name: 'C', value: 'C' },
                  { name: 'D', value: 'D' },
                  { name: 'E', value: 'E' },
                  { name: 'F', value: 'F' },
                ]"
                noValidation
              ></hl-Select>
            </ValidationProvider>
            <span v-else>{{ rentalObject.details.energyScore }}</span>
          </div>
          <hl-Mask v-else :width="40" :height="15"></hl-Mask>
        </div>
      </div>

      <div class="row mt-4">
        <div class="col">
          <strong>Oppvarmingskarakter</strong>
        </div>

        <div class="col">
          <div v-if="loaded">
            <ValidationProvider v-if="editMode" name="heatingCharacter">
              <hl-Select
                size="sm"
                :hasMarginBottom="false"
                v-model="localCopy.details.heatingScore"
                placeholder="velg oppvarmingskarakter"
                :options="[
                  { name: 'Mørkegrønn', value: 'Mørkegrønn' },
                  { name: 'Lysegrønn', value: 'Lysegrønn' },
                  { name: 'Gul', value: 'Gul' },
                  { name: 'Oransje', value: 'Oransje' },
                  { name: 'Rød', value: 'Rød' },
                ]"
                noValidation
              ></hl-Select>
            </ValidationProvider>

            <span v-else>
              <hl-Tag :customColor="getHeatingCharacterColor(rentalObject.details.heatingScore)">{{
                rentalObject.details.heatingScore
              }}</hl-Tag>
            </span>
          </div>

          <hl-Mask v-else :width="40" :height="15"></hl-Mask>
        </div>
      </div>

      <div class="row mt-4">
        <div class="col">
          <strong>Etasje</strong>
        </div>

        <div class="col">
          <div v-if="loaded">
            <ValidationProvider v-if="editMode" name="floor">
              <hl-Input
                type="number"
                size="sm"
                v-model="localCopy.details.floor"
                name="details.floor"
                nameForValidation="Etasje"
                v-validate
                vvScope="property"
              />
            </ValidationProvider>

            <span v-else>
              <span>{{ rentalObject.details.floor }}</span>
            </span>
          </div>

          <hl-Mask v-else :width="8" :height="15"></hl-Mask>
        </div>
      </div>

      <div class="row mt-4">
        <div class="col">
          <strong>Antall rom</strong>
        </div>

        <div class="col">
          <div v-if="loaded">
            <ValidationProvider
              v-if="editMode"
              name="Antall rom"
              :rules="validationRules.rentalObject.rentalObject.roomCount"
              v-slot="{ errors }"
            >
              <hl-Input type="number" size="sm" v-model="localCopy.details.roomCount" :error="errors[0]" />
            </ValidationProvider>

            <span v-else>
              <span>{{ rentalObject.details.roomCount }}</span>
            </span>
          </div>

          <hl-Mask v-else :width="8" :height="15"></hl-Mask>
        </div>
      </div>
    </ValidationObserver>
  </BlockContainer>
</template>

<script>
import BlockContainer from '@/components/BlockContainer.vue';
import CancelButton from '@/components/UI/buttons/CancelButton';
import EditButton from '@/components/UI/buttons/EditButton';
import SaveButton from '@/components/UI/buttons/SaveButton';
import addValidationRules from '@/mixins/addValidationRules.js';
import { mapGetters } from 'vuex';
import Input from '@modules/husleie-framework/src/components/forms/inputs/input/Input.vue';
import Mask from '@modules/husleie-framework/src/components/loaders/Mask.vue';
import Select from '@modules/husleie-framework/src/components/forms/inputs/select/Select.vue';
import Tag from '@modules/husleie-framework/src/components/forms/inputs/tags/Tag.vue';

export default {
  components: {
    BlockContainer,
    EditButton,
    CancelButton,
    SaveButton,
    'hl-Input': Input,
    'hl-Mask': Mask,
    'hl-Select': Select,
    'hl-Tag': Tag,
  },

  mixins: [addValidationRules],

  props: {
    rentalObjectId: [Number, String],
  },

  data() {
    return {
      editMode: false,
      loading: false,
      localCopy: null,
      propertyTypes: [
        {
          value: 'apartment',
          name: 'Leilighet',
        },
        {
          value: 'house',
          name: 'Enebolig',
        },
        {
          value: 'dorm',
          name: 'Hybel',
        },
        {
          value: 'co-op',
          name: 'Rom i bofellesskap',
        },
        {
          value: 'semi-detached',
          name: 'Tomannsbolig',
        },
        {
          value: 'chained-house',
          name: 'Rekkehus',
        },
        {
          value: 'commercial',
          name: 'Næringseiendom',
        },
        {
          value: 'garage-parking',
          name: 'Garasje / Parkering',
        },
      ],
    };
  },

  computed: {
    ...mapGetters(['getRentalObject', 'isExtendedRentalObjectLoaded']),

    rentalObject() {
      return this.getRentalObject(this.rentalObjectId);
    },

    isFormValid() {
      return !this.$validator.errors.items.length;
    },

    patchBody() {
      if (this.localCopy == null) return;
      return {
        details: {
          type: this.localCopy.details.type,
          areaSize: this.localCopy.details.areaSize,
          energyScore: this.localCopy.details.energyScore,
          heatingScore: this.localCopy.details.heatingScore,
          floor: this.localCopy.details.floor,
          roomCount: this.localCopy.details.roomCount,
          electricityMeter: this.rentalObject.details.electricityMeter,
          electricityMeterId: this.rentalObject.details.electricityMeterId,
        },
      };
    },

    loaded() {
      return this.isExtendedRentalObjectLoaded;
    },

    type() {
      return this.rentalObject.details.type;
    },

    energyScore() {
      return this.rentalObject.details.energyScore;
    },

    heatingScore() {
      return this.rentalObject.details.heatingScore;
    },
  },

  methods: {
    setEditModeAndLocalCopy() {
      this.editMode = true;
      this.$set(this, 'localCopy', JSON.parse(JSON.stringify(this.rentalObject)));
    },

    validate() {
      this.$validator.validate('property.*');
    },

    getHeatingCharacterColor(color) {
      if (color === 'Mørkegrønn') {
        return '#00A751';
      }

      if (color === 'Lysegrønn') {
        return '#C2D82E';
      }

      if (color === 'Gul') {
        return '#FEF300';
      }

      if (color === 'Oransje') {
        return '#FDBA0A';
      }

      if (color === 'Rød') {
        return '#EE141E';
      }
    },

    setType(e) {
      this.$store.dispatch('updatePropertyType', {
        rentalObjectId: this.rentalObjectId,
        type: e.target.value,
      });
    },

    setEnergyScore(e) {
      this.$store.dispatch('updateEnergyScore', {
        rentalObjectId: this.rentalObjectId,
        score: e.target.value,
      });
    },

    setHeatingScore(e) {
      this.$store.dispatch('updateHeatingScore', {
        rentalObjectId: this.rentalObjectId,
        score: e.target.value,
      });
    },

    patchRentalObject() {
      this.loading = true;
      this.$store.dispatch('patchRentalObject', {
        rentalObjectId: this.rentalObjectId,
        body: this.patchBody,
        success: () => {
          this.editMode = false;
          this.$toast('Lagret', 'success');
        },
        error: () => {
          this.showErrorModal = true;
          this.$toast('Kunne ikke lagre', 'error');
        },
      });
      this.loading = false;
    },

    mapPropertyTypes(type) {
      for (const i of this.propertyTypes) {
        if (i.value === type) {
          return i.name;
        }
      }
    },
  },
};
</script>

<style scoped lang="scss">
.row {
  min-height: 31px;
  align-items: center;
}
</style>
